import React, { useEffect, useRef, useState } from "react";
import { Percent, X } from "lucide-react";

import { NumberInput } from "./UI";
import { lineWastageSummary, WASTAGE_SOURCE_LABEL } from "../lib/recipe";

/* LT-20 — line-tier wastage override (line → recipe → material → 0%).
 * Rendered under the material-rate cell of a library line that has recipe
 * material entries. Blank = inherit (placeholder shows the inherited value
 * and the source tier is named); a typed value is an override with distinct
 * styling and a one-click clear. Changing it rebuilds the material rate via
 * `applyLineWastageOverride` in the parent (cost follows quantity). */
export function LineWastageBadge({ line, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const summary = lineWastageSummary(line);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  if (!summary) return null;
  const isOverride = line.wastage_pct_override !== null && line.wastage_pct_override !== undefined;
  const inherited = lineWastageSummary({ ...line, wastage_pct_override: null });
  const pctText = summary.pct == null ? "varies" : `${summary.pct}%`;
  const SHORT = { line: "line", recipe: "recipe", material: "material", default_zero: "none" };
  const suffix = summary.source === "mixed" ? "" : ` · ${SHORT[summary.source]}`;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setOpen((x) => !x)}
        data-testid={`line-wastage-badge-${line.id}`}
        title={`Wastage ${pctText} — ${summary.label}`}
        className={[
          "mt-0.5 inline-flex items-center gap-0.5 rounded px-1 h-4 whitespace-nowrap transition-colors",
          isOverride ? "bg-[#FBEAD2] text-[#B45309] font-semibold" : "text-ink-4 hover:text-ink-2",
        ].join(" ")}
        style={{ fontSize: 9.5 }}
      >
        <Percent size={9} /> {pctText}{suffix}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-40 bg-surface border border-line rounded-md shadow-card p-3"
          style={{ width: 300 }}
          data-testid={`line-wastage-popover-${line.id}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="col-head" style={{ fontWeight: 700 }}>Wastage on this line</span>
            <button type="button" onClick={() => setOpen(false)} className="text-ink-3 hover:text-ink"><X size={12} /></button>
          </div>
          <div className={isOverride ? "rounded-md ring-2 ring-[#D97706]/50" : ""}>
            <NumberInput
              value={isOverride ? line.wastage_pct_override : null}
              step="0.5"
              suffix="%"
              placeholder={inherited && inherited.pct != null ? String(inherited.pct) : "varies"}
              onChange={(v) => onChange(v)}
              data-testid={`line-wastage-input-${line.id}`}
            />
          </div>
          <p className={["mt-1.5", isOverride ? "text-[#B45309] font-medium" : "text-ink-3"].join(" ")} style={{ fontSize: 11 }}
             data-testid={`line-wastage-source-${line.id}`}>
            {isOverride
              ? `${line.wastage_pct_override}% ${WASTAGE_SOURCE_LABEL.line} — replaces every material's own wastage on this line`
              : inherited
                ? (inherited.pct == null ? "Inherited — varies by material (see below)" : `${inherited.pct}% ${inherited.label}`)
                : "No material entries"}
          </p>
          {isOverride && (
            <button
              type="button"
              onClick={() => onChange(null)}
              data-testid={`line-wastage-clear-${line.id}`}
              className="mt-1 text-accent hover:underline"
              style={{ fontSize: 11 }}
            >
              Clear override — inherit again
            </button>
          )}
          <div className="mt-2 border-t border-line-soft pt-2 space-y-0.5">
            {(inherited ? inherited.entries : []).map((e, i) => (
              <div key={i} className="flex items-baseline justify-between gap-2 text-ink-3" style={{ fontSize: 10.5 }}>
                <span className="truncate">{e.description || "(material)"}</span>
                <span className="whitespace-nowrap">{e.pct}% · {e.source === "recipe" ? "recipe" : e.source === "material" ? "material" : "0%"}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-ink-4" style={{ fontSize: 10 }}>
            Wastage is applied to quantity: net → wastage → gross → rate → cost. Blank inherits; it never means 0.
          </p>
        </div>
      )}
    </div>
  );
}
